'use client';

import { useState } from 'react';
import Link from 'next/link';
import { motion } from 'motion/react';
import { 
  ChevronRight, Layers, Smartphone, Tv, Monitor, Apple, 
  Copy, Check, Info, AlertTriangle, MessageCircle, ArrowRight,
  Wifi, Shield, Key, Download, PlayCircle
} from 'lucide-react';

// CopyCode Component
const CopyCode = ({ code }: { code: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button 
      onClick={handleCopy}
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/20 transition-colors group"
    >
      <span className="font-mono text-cyan-400 font-bold tracking-wider">{code}</span>
      {copied ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <Copy className="w-4 h-4 text-cyan-500 group-hover:text-cyan-400" />
      )}
    </button>
  );
};

export default function TutorialsPage() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1b3c53]/40 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-8">
            <Link href="/" className="hover:text-white transition-colors">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Tutoriels d'installation</span>
          </div>

          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[#7aaace] text-sm font-medium mb-6">
              <Layers className="w-4 h-4" /> Guides pas à pas
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">Tutoriels </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7aaace] to-[#1b3c53]">d'installation</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Suivez nos guides détaillés pour installer et configurer votre abonnement IPTV sur n'importe quel appareil en quelques minutes.
            </p>
          </div>

          {/* Quick Nav Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { id: 'tutorial-android', name: 'Android / TV Box', icon: Smartphone, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' },
              { id: 'tutorial-smarttv', name: 'Smart TV', icon: Tv, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
              { id: 'tutorial-ios', name: 'iOS / Apple', icon: Apple, color: 'text-[#7aaace]', bg: 'bg-[#7aaace]/10', border: 'border-[#7aaace]/20' },
              { id: 'tutorial-webplayer', name: 'Web Player', icon: Monitor, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' }
            ].map((nav) => (
              <button 
                key={nav.id}
                onClick={() => scrollToSection(nav.id)}
                className={`flex flex-col items-center justify-center p-6 rounded-2xl border ${nav.border} ${nav.bg} hover:bg-white/5 transition-all hover:-translate-y-1`}
              >
                <nav.icon className={`w-8 h-8 ${nav.color} mb-3`} />
                <span className="text-white font-medium">{nav.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tutorial 1: Android */}
      <section id="tutorial-android" className="py-24 border-b border-white/5 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-green-500" />
            </div>
            <h2 className="text-3xl font-bold text-white">Android (TV, Box, Smartphone)</h2>
          </div>

          {/* App Versions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {[
              { name: 'ESIPTV Pro+', desc: 'Play Store direct', code: null, badge: 'Play Store' },
              { name: 'ES MAX', desc: 'Nouvelle version', code: '7901322', badge: 'Nouveau' },
              { name: 'ESIPTV Line V1', desc: 'Version classique', code: '1124528', badge: null },
              { name: 'ESIPTV Line V2', desc: 'Version améliorée', code: '3038639', badge: null }
            ].map((app, i) => (
              <div key={i} className="bg-[#111] border border-white/10 rounded-xl p-5 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-white">{app.name}</h3>
                    {app.badge && (
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${app.badge === 'Nouveau' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
                        {app.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">{app.desc}</p>
                </div>
                {app.code && <CopyCode code={app.code} />}
              </div>
            ))}
          </div>

          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-12 flex gap-4">
            <Info className="w-6 h-6 text-blue-400 shrink-0" />
            <p className="text-sm text-blue-200">
              <strong className="text-blue-400">Note importante :</strong> Les versions "Line" utilisent un nom d'utilisateur et un mot de passe. La version "Pro+" utilise un code d'activation unique.
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-6 mb-8">
            <h3 className="text-xl font-semibold text-white mb-6">Installation via Downloader</h3>
            {[
              "Allez dans le Play Store de votre appareil et installez l'application 'Downloader'.",
              "Ouvrez les paramètres de votre appareil > Sécurité et restrictions > Sources inconnues, et autorisez 'Downloader'.",
              "Ouvrez l'application Downloader.",
              "Dans la barre d'URL, entrez le code de l'application souhaitée (ex: 7901322 pour ES MAX).",
              "Cliquez sur 'Go'. Le téléchargement commencera automatiquement.",
              "Une fois téléchargé, cliquez sur 'Installer', puis ouvrez l'application et entrez vos identifiants."
            ].map((step, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center shrink-0 text-green-400 font-bold">
                  {i + 1}
                </div>
                <p className="text-gray-300 pt-1">{step}</p>
              </div>
            ))}
          </div>

          {/* Tip Box */}
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex gap-4">
            <Wifi className="w-6 h-6 text-green-400 shrink-0" />
            <p className="text-sm text-green-200">
              <strong className="text-green-400">Astuce :</strong> Assurez-vous d'avoir une connexion internet stable (Ethernet recommandé pour les Box TV) avant de commencer le téléchargement.
            </p>
          </div>
        </div>
      </section>

      {/* Tutorial 2: Smart TV */}
      <section id="tutorial-smarttv" className="py-24 border-b border-white/5 scroll-mt-20 bg-[#0c0c0c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Tv className="w-6 h-6 text-blue-500" />
            </div>
            <h2 className="text-3xl font-bold text-white">Smart TV (Samsung & LG)</h2>
          </div>

          <p className="text-gray-400 mb-8 text-lg">
            Notre application officielle n'étant pas disponible nativement sur les stores Samsung et LG, nous utilisons <strong>HotPlayer</strong> comme alternative officielle et optimisée.
          </p>

          {/* Steps */}
          <div className="space-y-6 mb-12">
            {[
              "Allumez votre TV et ouvrez le magasin d'applications (Content Store pour LG, Apps pour Samsung).",
              "Recherchez l'application 'HotPlayer' et installez-la.",
              "Lancez HotPlayer une fois l'installation terminée.",
              "Sur l'écran d'accueil de l'application, votre 'Adresse MAC' (MAC Address) s'affichera.",
              "Envoyez-nous cette adresse MAC via WhatsApp pour que nous puissions activer votre abonnement à distance."
            ].map((step, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center shrink-0 text-blue-400 font-bold">
                  {i + 1}
                </div>
                <p className="text-gray-300 pt-1">{step}</p>
              </div>
            ))}
          </div>

          {/* Warning Box */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-8 flex gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />
            <p className="text-sm text-amber-200">
              <strong className="text-amber-400">Attention :</strong> Sans votre adresse MAC, nous ne pouvons pas activer votre abonnement. Veuillez vérifier attentivement les chiffres et les lettres avant de nous l'envoyer.
            </p>
          </div>

          {/* WhatsApp CTA */}
          <a 
            href="https://wa.me/33745561646?text=Bonjour,%20voici%20mon%20adresse%20MAC%20pour%20activation%20:%20[VOTRE ADRESSE MAC]" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full py-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition-colors"
          >
            <MessageCircle className="w-6 h-6" />
            Envoyez votre adresse MAC pour activation
          </a>
        </div>
      </section>

      {/* Tutorial 3: iOS */}
      <section id="tutorial-ios" className="py-24 border-b border-white/5 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#7aaace]/20 flex items-center justify-center">
              <Apple className="w-6 h-6 text-[#7aaace]" />
            </div>
            <h2 className="text-3xl font-bold text-white">iOS (iPhone / iPad)</h2>
          </div>

          <p className="text-gray-400 mb-8 text-lg">
            Pour les appareils Apple, nous recommandons l'utilisation de l'application <strong>000 Player</strong> disponible gratuitement sur l'App Store.
          </p>

          {/* Steps */}
          <div className="space-y-6 mb-12">
            {[
              "Ouvrez l'App Store sur votre iPhone ou iPad.",
              "Recherchez '000 Player' et téléchargez l'application.",
              "Ouvrez l'application et entrez le nom d'utilisateur et le mot de passe que vous avez reçus par email.",
              "Cliquez sur 'Login' et profitez de vos chaînes."
            ].map((step, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#7aaace]/20 border border-[#7aaace]/30 flex items-center justify-center shrink-0 text-[#7aaace] font-bold">
                  {i + 1}
                </div>
                <p className="text-gray-300 pt-1">{step}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-gray-500 mr-2 py-1">Appareils compatibles :</span>
            {['iPhone', 'iPad', 'iPad Air', 'iPad Pro', 'iPad Mini'].map((device, i) => (
              <span key={i} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-gray-400">
                {device}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Tutorial 4: Web Player */}
      <section id="tutorial-webplayer" className="py-24 border-b border-white/5 scroll-mt-20 bg-[#0c0c0c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <Monitor className="w-6 h-6 text-orange-500" />
            </div>
            <h2 className="text-3xl font-bold text-white">Web Player (PC / Mac)</h2>
          </div>

          <p className="text-gray-400 mb-8 text-lg">
            Regardez directement depuis votre navigateur web, aucune installation d'application n'est requise.
          </p>

          {/* Steps */}
          <div className="space-y-6 mb-12">
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center shrink-0 text-orange-400 font-bold">1</div>
              <p className="text-gray-300 pt-1 flex flex-wrap items-center gap-2">
                Allez sur le site <a href="http://my.splayer.in" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline inline-flex items-center gap-1">my.splayer.in <ArrowRight className="w-3 h-3" /></a>
              </p>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center shrink-0 text-orange-400 font-bold">2</div>
              <div className="text-gray-300 pt-1 flex flex-wrap items-center gap-2">
                Entrez le code serveur suivant : <CopyCode code="998877" />
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center shrink-0 text-orange-400 font-bold">3</div>
              <p className="text-gray-300 pt-1">
                Entrez vos identifiants (nom d'utilisateur et mot de passe), connectez-vous et profitez !
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-gray-500 mr-2 py-1">Navigateurs compatibles :</span>
            {['Chrome', 'Firefox', 'Safari', 'Edge', 'Opera'].map((browser, i) => (
              <span key={i} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-gray-400">
                {browser}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* General Tips Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Conseils Généraux</h2>
            <p className="text-gray-400">Pour une expérience de streaming optimale.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Connexion stable', desc: 'Utilisez un câble Ethernet plutôt que le Wi-Fi si possible.', icon: Wifi, color: 'text-blue-400', bg: 'bg-blue-500/10' },
              { title: 'VPN recommandé', desc: 'Utile si votre FAI bride la bande passante IPTV.', icon: Shield, color: 'text-green-400', bg: 'bg-green-500/10' },
              { title: 'Identifiants', desc: 'Ne partagez jamais vos identifiants, sous peine de blocage.', icon: Key, color: 'text-[#7aaace]', bg: 'bg-[#7aaace]/10' },
              { title: 'Mises à jour', desc: 'Gardez toujours vos applications à jour.', icon: Download, color: 'text-orange-400', bg: 'bg-orange-500/10' },
              { title: 'Qualité vidéo', desc: 'Une connexion de 15 Mbps minimum est requise pour la 4K.', icon: PlayCircle, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
              { title: 'Support 24/7', desc: 'Contactez-nous sur WhatsApp en cas de problème.', icon: MessageCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10' }
            ].map((tip, i) => (
              <div key={i} className="bg-[#111] border border-white/5 rounded-2xl p-6 flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl ${tip.bg} flex items-center justify-center shrink-0`}>
                  <tip.icon className={`w-6 h-6 ${tip.color}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">{tip.title}</h3>
                  <p className="text-sm text-gray-400">{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-[2.5rem] p-12 text-center border border-white/10 relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">Besoin d'aide pour l'installation ?</h2>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a href="https://wa.me/33745561646" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-xl transition-all hover:shadow-[0_0_20px_rgba(37,211,102,0.4)] flex items-center justify-center gap-2">
                  <MessageCircle className="w-5 h-5" /> Contacter le support
                </a>
                <Link href="/#subscriptions" className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl border border-white/10 transition-all">
                  Voir les abonnements
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
